import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import {
  Pageable,
  WrapperResponse,
  WrapperPageableResponse,
  SearchInvoiceFilterSpec,
  ListInvoiceFilterSpec
} from './service-common';
import type {
  BillingTypeEnumValue,
  CollectionMethodEnumValue,
  FrequencyEnumValue,
  DaySelectionEnumValue,
  InvoiceStatusEnumValue,
  TaxApplicationTypeEnumValue
} from '../core/models';
import {
  type ChargeDTO,
  type CreateInvoiceBillingTerms,
  type CreateInvoiceLineRequest,
  type CreateInvoiceLineTaxRequest,
  type CreateInvoiceRequest,
  type PreviewInvoiceLineRequest,
  type InvoiceDTO,
  type InvoiceLineDTO,
  type TaxDTO,
  type MeasureUnitDTO,
  type TaxProductCodeDTO,
  type ProductDTO,
  type SATCFDIUseDTO,
  type BillingTermsDTO,
  type CustomerDTO,
  type WrapperResponseInvoiceDTO,
  type WrapperResponseListInvoiceDTO,
  type WrapperPageableResponseInvoiceDTO,
  type WrapperResponseListChargeDTO,
  type WrapperResponseReadFileResponse,
  type WrapperResponseVoid,
  type SaleDTO
} from '../core/models';
import { parseContentDispositionFilename } from '../core/api-response-utils';

/** Detalle público de venta (sale + cargos) para /public/sales/:id y /public/charges/:id */
export interface PublicInvoiceDetailDTO {
  sale: SaleDTO;
  charges: ChargeDTO[];
  companyLogoUrl?: string;
}

export type WrapperResponsePublicInvoiceDetailDTO = WrapperResponse<PublicInvoiceDetailDTO>;

/** Alias para compatibilidad con código que importaba WrapperResponseChargeListDTO */
export type WrapperResponseChargeListDTO = WrapperResponseListChargeDTO;

// Re-exportar tipos desde core/models para compatibilidad con componentes
export type BillingType = BillingTypeEnumValue;
export type CollectionMethod = CollectionMethodEnumValue;
export type BillingFrequency = FrequencyEnumValue;
export type DaySelection = DaySelectionEnumValue;
export type InvoiceStatus = InvoiceStatusEnumValue;
export type TaxApplicationType = TaxApplicationTypeEnumValue;

export type {
  ChargeDTO,
  CreateInvoiceBillingTerms,
  CreateInvoiceLineRequest,
  CreateInvoiceLineTaxRequest,
  CreateInvoiceRequest,
  PreviewInvoiceLineRequest,
  InvoiceDTO,
  InvoiceLineDTO,
  TaxDTO,
  MeasureUnitDTO,
  TaxProductCodeDTO,
  ProductDTO,
  SATCFDIUseDTO,
  BillingTermsDTO,
  CustomerDTO,
  WrapperResponseInvoiceDTO,
  WrapperResponseListInvoiceDTO,
  WrapperPageableResponseInvoiceDTO,
  WrapperResponseListChargeDTO
} from '../core/models';

@Injectable({
  providedIn: 'root'
})
export class InvoiceService {
  private http = inject(HttpClient);
  private apiUrl = environment.apiUrl;

  searchInvoices(
    spec: SearchInvoiceFilterSpec = {},
    pageable: Pageable = { page: 0, size: 20 }
  ): Observable<WrapperResponseListInvoiceDTO> {
    let params = new HttpParams();

    Object.entries(spec).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params = params.append(key, String(value));
      }
    });

    params = params.append('page', String(pageable.page));
    params = params.append('size', String(pageable.size));

    if (pageable.sort && pageable.sort.length > 0) {
      pageable.sort.forEach(sort => {
        params = params.append('sort', sort);
      });
    }

    return this.http.get<WrapperResponseListInvoiceDTO>(`${this.apiUrl}/invoices`, { params });
  }

  listInvoices(
    spec: ListInvoiceFilterSpec = {},
    pageable: Pageable = { page: 0, size: 20 }
  ): Observable<WrapperPageableResponseInvoiceDTO> {
    let params = new HttpParams();

    Object.entries(spec).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      if (Array.isArray(value)) {
        value.forEach(v => {
          if (v !== undefined && v !== null && v !== '') {
            params = params.append(key, String(v));
          }
        });
      } else if (value !== '') {
        params = params.append(key, String(value));
      }
    });

    params = params.append('page', String(pageable.page));
    params = params.append('size', String(pageable.size));

    if (pageable.sort && pageable.sort.length > 0) {
      pageable.sort.forEach(sort => {
        params = params.append('sort', sort);
      });
    }

    return this.http.get<WrapperPageableResponseInvoiceDTO>(`${this.apiUrl}/invoices`, { params });
  }

  getInvoiceById(id: number): Observable<WrapperResponseInvoiceDTO> {
    return this.http.get<WrapperResponseInvoiceDTO>(`${this.apiUrl}/invoices/${id}`);
  }

  /**
   * Obtiene el detalle público de una factura y sus cargos (sin autenticación).
   * Requiere el query param hash.
   */
  getPublicInvoiceDetail(saleId: number, hash: string): Observable<WrapperResponsePublicInvoiceDetailDTO> {
    const params = new HttpParams().set('hash', hash);
    return this.http.get<WrapperResponsePublicInvoiceDetailDTO>(
      `${this.apiUrl}/public/sales/${saleId}`,
      { params }
    );
  }

  /**
   * Obtiene el detalle público de factura y cargos para el recibo de un cargo (sin autenticación).
   * Requiere chargeId y query param hash (mismo token que la factura).
   */
  getPublicChargeReceiptDetail(chargeId: number, hash: string): Observable<WrapperResponsePublicInvoiceDetailDTO> {
    const params = new HttpParams().set('hash', hash);
    return this.http.get<WrapperResponsePublicInvoiceDetailDTO>(
      `${this.apiUrl}/public/charges/${chargeId}`,
      { params }
    );
  }

  /**
   * Genera un hash público para un cargo específico.
   */
  createChargePublicHash(chargeId: number, days: number = 1): Observable<WrapperResponse<string>> {
    const params = new HttpParams().set('days', String(days));
    return this.http.get<WrapperResponse<string>>(
      `${this.apiUrl}/charges/${chargeId}/public-hash`,
      { params }
    );
  }

  getInvoiceCharges(invoiceId: number): Observable<WrapperResponseListChargeDTO> {
    return this.http.get<WrapperResponseListChargeDTO>(`${this.apiUrl}/invoices/${invoiceId}/charges`);
  }

  createInvoice(request: CreateInvoiceRequest): Observable<WrapperResponseInvoiceDTO> {
    return this.http.post<WrapperResponseInvoiceDTO>(`${this.apiUrl}/invoices`, request);
  }

  updateInvoice(id: number, request: CreateInvoiceRequest): Observable<WrapperResponseInvoiceDTO> {
    return this.http.put<WrapperResponseInvoiceDTO>(`${this.apiUrl}/invoices/${id}`, request);
  }

  addInvoiceLine(invoiceId: number, request: CreateInvoiceLineRequest): Observable<WrapperResponseInvoiceDTO> {
    return this.http.post<WrapperResponseInvoiceDTO>(`${this.apiUrl}/invoices/${invoiceId}/lines`, request);
  }

  updateInvoiceLine(
    invoiceId: number,
    lineId: number,
    request: CreateInvoiceLineRequest
  ): Observable<WrapperResponseInvoiceDTO> {
    return this.http.put<WrapperResponseInvoiceDTO>(
      `${this.apiUrl}/invoices/${invoiceId}/lines/${lineId}`,
      request
    );
  }

  removeInvoiceLine(invoiceId: number, lineId: number): Observable<WrapperResponseInvoiceDTO> {
    return this.http.delete<WrapperResponseInvoiceDTO>(
      `${this.apiUrl}/invoices/${invoiceId}/lines/${lineId}`
    );
  }

  /**
   * Preview de una línea: calcula impuestos y totales sin persistencia.
   * Devuelve la línea con subtotal, taxAmount, totalAmount y taxes recalculados.
   */
  previewInvoiceLine(request: PreviewInvoiceLineRequest): Observable<WrapperResponse<InvoiceLineDTO>> {
    return this.http.post<WrapperResponse<InvoiceLineDTO>>(
      `${this.apiUrl}/invoices/lines/preview`,
      request
    );
  }

  deleteInvoice(id: number): Observable<WrapperResponseInvoiceDTO> {
    return this.http.delete<WrapperResponseInvoiceDTO>(`${this.apiUrl}/invoices/${id}`);
  }

  /**
   * Emite/timbra el CFDI de una factura proforma emitida.
   * Requiere que la factura esté en estado ISSUED y con billingType PROFORMA.
   */
  stampInvoice(id: number): Observable<WrapperResponseInvoiceDTO> {
    return this.http.post<WrapperResponseInvoiceDTO>(`${this.apiUrl}/invoices/${id}/stamp`, {});
  }

  /**
   * Genera un hash público para la factura. El hash permite ver el detalle de la factura
   * en la ruta pública /public/sales/:id?hash=XXX sin autenticación.
   */
  createPublicHash(id: number, days: number = 1): Observable<WrapperResponse<string>> {
    const params = new HttpParams().set('days', String(days));
    return this.http.get<WrapperResponse<string>>(
      `${this.apiUrl}/sales/${id}/public-hash`,
      { params }
    );
  }

  /**
   * Obtiene la URL firmada para descargar el PDF del CFDI timbrado.
   * body.content contiene la URL de descarga.
   */
  getStampPDF(invoiceId: number): Observable<WrapperResponseReadFileResponse> {
    return this.http.get<WrapperResponseReadFileResponse>(
      `${this.apiUrl}/invoices/${invoiceId}/stamp/pdf`
    );
  }

  /**
   * Obtiene la URL firmada para descargar el XML del CFDI timbrado.
   * body.content contiene la URL de descarga.
   */
  getStampXML(invoiceId: number): Observable<WrapperResponseReadFileResponse> {
    return this.http.get<WrapperResponseReadFileResponse>(
      `${this.apiUrl}/invoices/${invoiceId}/stamp/xml`
    );
  }

  /** Descarga del PDF por bytes (GET /invoices/{id}/stamp/pdf/download) — evita CORS de URL firmada. */
  downloadStampPdfFile(invoiceId: number): Observable<{ blob: Blob; filename: string }> {
    return this.http
      .get(`${this.apiUrl}/invoices/${invoiceId}/stamp/pdf/download`, {
        responseType: 'blob',
        observe: 'response'
      })
      .pipe(
        map((resp) => ({
          blob: resp.body as Blob,
          filename:
            parseContentDispositionFilename(resp.headers.get('Content-Disposition')) ??
            `CFDI-${invoiceId}.pdf`
        }))
      );
  }

  /** Descarga del XML por bytes (GET /invoices/{id}/stamp/xml/download). */
  downloadStampXmlFile(invoiceId: number): Observable<{ blob: Blob; filename: string }> {
    return this.http
      .get(`${this.apiUrl}/invoices/${invoiceId}/stamp/xml/download`, {
        responseType: 'blob',
        observe: 'response'
      })
      .pipe(
        map((resp) => ({
          blob: resp.body as Blob,
          filename:
            parseContentDispositionFilename(resp.headers.get('Content-Disposition')) ??
            `CFDI-${invoiceId}.xml`
        }))
      );
  }

  /** Reenvía el CFDI (PDF y XML) al correo indicado (POST /invoices/{invoiceId}/stamp/resend). */
  resendCfdi(invoiceId: number, email: string): Observable<WrapperResponseVoid> {
    return this.http.post<WrapperResponseVoid>(`${this.apiUrl}/invoices/${invoiceId}/stamp/resend`, { email });
  }

  /** Cancela un CFDI vigente (POST /invoices/{invoiceId}/stamp/cancel). */
  cancelCfdi(
    invoiceId: number,
    request: { motivo: string; folioSustitucion?: string }
  ): Observable<WrapperResponseInvoiceDTO> {
    return this.http.post<WrapperResponseInvoiceDTO>(
      `${this.apiUrl}/invoices/${invoiceId}/stamp/cancel`,
      request
    );
  }

  /** Exporta facturas filtradas a CSV (GET /invoices/export). */
  exportInvoicesCsv(
    spec: ListInvoiceFilterSpec = {},
    sort: string[] = ['issueDate,desc']
  ): Observable<{ blob: Blob; filename: string }> {
    let params = new HttpParams();
    Object.entries(spec).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      if (Array.isArray(value)) {
        value.forEach((v) => {
          if (v !== undefined && v !== null && v !== '') {
            params = params.append(key, String(v));
          }
        });
      } else if (value !== '') {
        params = params.append(key, String(value));
      }
    });
    sort.forEach((s) => {
      params = params.append('sort', s);
    });
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timeZone) {
      params = params.append('timeZone', timeZone);
    }

    return this.http
      .get(`${this.apiUrl}/invoices/export`, {
        params,
        responseType: 'blob',
        observe: 'response'
      })
      .pipe(
        map((resp) => ({
          blob: resp.body as Blob,
          filename:
            parseContentDispositionFilename(resp.headers.get('Content-Disposition')) ?? 'facturas.csv'
        }))
      );
  }
}
